import {
  Field,
  Image,
  ImageField,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { DynamicHeading } from '@gn-audio/jss-components';

import { mapSharedPropertiesToFields } from 'common/getters';
import { withTheme } from 'common/jabraTheme/withTheme';
import { classNames } from 'common/utils/classNames';
import NavButton from 'composables/shared/NavButton/NavButton';
import { ComponentProps } from 'lib/component-props';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';

type TopBannerSize = 'small' | 'medium' | 'large';

type TopBannerFields = {
  title: Field<string>;
  titleSecondary: Field<string>;
  details: Field<string>;
  size: Field<TopBannerSize>;
  backgroundColor: Field<string>;
  backgroundImage: ImageField;
  primaryButtonText: Field<string>;
  primaryButtonLink: LinkField;
  secondaryButtonText: Field<string>;
  secondaryButtonLink: LinkField;
  isMainHeading: Field<boolean>;
};
type TopBannerProps = ComponentProps & {
  fields: TopBannerFields;
};

const imageSizes: { [key in TopBannerSize]: { h: number; w: number } } = {
  small: {
    h: 420,
    w: 708,
  },
  medium: {
    h: 640,
    w: 708,
  },
  large: {
    h: 900,
    w: 920,
  },
};

type classNamesBySize = Partial<{ [key in TopBannerSize]: string }>;

const secondaryTitleClassNames: classNamesBySize = {
  small: 'text-base',
  medium: 'text-base',
  large: 'text-xl',
};

const buttonContainerClassNames: classNamesBySize = {
  small: 'pt-8',
  medium: 'pt-8',
  large: 'pt-10',
};

const TopBanner = ({ fields }: TopBannerProps) => {
  const { isBelowMd } = useBreakpoint('md', true);
  const size = fields.size.value;
  const buttonSize = size !== 'small' || isBelowMd ? 'l' : 'm';

  const {
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonLink,
    secondaryButtonText,
    backgroundImage,
    details,
    title,
    titleSecondary,
    isMainHeading,
  } = mapSharedPropertiesToFields<TopBannerFields>(fields);

  const doesHavePrimaryButton = primaryButtonText?.value && !!primaryButtonLink?.value?.href;
  const doesHaveSecondaryButton = secondaryButtonText?.value && !!secondaryButtonLink?.value?.href;

  return (
    <div className={classNames('bg-theme relative flex justify-end')}>
      <div className="left-0 right-0 z-20 m-auto flex h-full w-full max-xl:container-fluid xl:container max-xl:flex-col xl:absolute xl:items-center">
        <div className="container-left flex-shrink-0 py-0 xl:hidden">
          <Image
            field={{
              src: backgroundImage.value?.src,
              alt: backgroundImage.value?.alt,
            }}
            className="h-[unset] max-h-[550px] w-full rounded-l-full object-cover max-sm:max-h-[400px]"
            height={imageSizes[size].h}
            width={imageSizes[size].w}
          />
        </div>
        <div className="basis-1/2 pb-16 pt-8 max-xl:container md:pr-6">
          {titleSecondary.value && (
            <>
              <span
                className={classNames(
                  secondaryTitleClassNames[size],
                  'paragraph mb-0 block pb-2 uppercase'
                )}
              >
                {titleSecondary.value}
              </span>
              <div className="mb-2 h-0.5 w-24 bg-yellow" />
            </>
          )}

          <DynamicHeading className={classNames('heading-1')} isMainHeading={isMainHeading.value}>
            {title?.value}
          </DynamicHeading>

          <p className={classNames('paragraph-large')}>{details.value}</p>
          <div className={classNames(buttonContainerClassNames[size], 'flex flex-wrap gap-6')}>
            {doesHavePrimaryButton && (
              <NavButton
                variant="primary"
                size={buttonSize}
                link={primaryButtonLink}
                title={primaryButtonText?.value}
              />
            )}
            {doesHaveSecondaryButton && (
              <NavButton
                variant="secondary"
                size={buttonSize}
                link={secondaryButtonLink}
                title={secondaryButtonText?.value}
              />
            )}
          </div>
        </div>
      </div>
      <div style={{ height: `${imageSizes[size].h}px` }} className="basis-1/2 max-xl:hidden">
        <Image
          field={{
            src: backgroundImage.value?.src,
            alt: backgroundImage.value?.alt,
          }}
          className="h-full rounded-l-full object-cover"
          height={imageSizes[size].h}
          width="100%"
        />
      </div>
    </div>
  );
};

export default withTheme({ legacyBackgroundFieldName: 'backgroundColor' })(
  withDatasourceCheck()(TopBanner)
);
